/**
 * What: Local store for stationId-sessionId key-value pairs.
 *
 * Why: For Start Music Stations on Song Start [DTC-478],
 * we need to send content_start param to stream request url
 * only on first listen of a Live Music Station within a session.
 * StationSessions allows us to check whether a user is listening
 * to the same station within the same session.
 *
 * How: StationSessions is a singleton class, whose instance
 * uses a Map to store stationId-sessionId key-value pairs.
 * Call `setStationSessionId` whenever content_start is appended
 * to stream request url to record that user has been started on content.
 * Call `isNewStationSession` on subsequent stream requests
 * to check if content_start param should be added to stream request urls.
 *
 * Note: Since the class is initialized on app launch, hard reload
 * or refresh will re-initialize the class instance and clear the map.
 */

interface ILiveStationSessions {
  setLiveStationSessionId: (stationId: string, sessionId: string | undefined) => void;
  isNewStationSession: (stationId: string, sessionId: string | undefined) => boolean;
  getLiveStationSessionId: (stationId: string) => string | undefined;
  setLiveStationTimestamp: (stationId: string, timestamp: number) => void;
  getLiveStationTimestamp: (stationId: string) => number | null;
  isLiveStationResumable: (stationId: string, timestamp: number) => boolean;
  clearLiveStationSessions: () => void;
}

const defaultResumableWindowMins = 5;

class LiveStationSessions implements ILiveStationSessions {
  #liveStationSessionIds: Map<string, string>;
  #liveStationTimestamps: Map<string, number>;
  #resumableWindowMins: number;

  constructor(resumableWindowMins: number) {
    this.#liveStationSessionIds = new Map();
    this.#liveStationTimestamps = new Map();
    this.#resumableWindowMins = resumableWindowMins;
  }

  // Sets stationId-sessionId as key-value pair in the stationSessions
  public setLiveStationSessionId(stationId: string, sessionId: string | undefined): void {
    this.#liveStationSessionIds.set(stationId, sessionId ?? '');
  }

  // Gets sessionId value for the stationId key
  public getLiveStationSessionId(stationId: string): string | undefined {
    return this.#liveStationSessionIds.get(stationId);
  }

  // Checks whether the user is listening to the same station in the same session
  public isNewStationSession(stationId: string, currentSessionId: string | undefined): boolean {
    const prevSessionId = this.getLiveStationSessionId(stationId) ?? '';
    return currentSessionId !== prevSessionId;
  }

  // Sets stationId-timestamp as key-value pair in the stationSessions
  public setLiveStationTimestamp(stationId: string, timestamp: number): void {
    this.#liveStationTimestamps.set(stationId, timestamp);
  }

  // Gets timestamp for the stationId
  public getLiveStationTimestamp(stationId: string): number | null {
    return this.#liveStationTimestamps.get(stationId) ?? null;
  }

  public isLiveStationResumable(stationId: string, currentTimestamp: number): boolean {
    const previousTimestamp = this.getLiveStationTimestamp(stationId);
    if (!previousTimestamp) {
      return false;
    }
    const resumableWindowMs = this.#resumableWindowMins * 60 * 1000;
    return (currentTimestamp - previousTimestamp) < resumableWindowMs;
  }

  // Clears sessionIds and timestamps maps
  public clearLiveStationSessions(): void {
    this.#liveStationSessionIds.clear();
    this.#liveStationTimestamps.clear();
  }
}

const liveStationSessionsInstance = Object.freeze(
  new LiveStationSessions(defaultResumableWindowMins),
);

export default liveStationSessionsInstance;
